import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  confirmOrderPaidApi,
  fetchAdminOrdersApi,
  fetchAdminReviewsApi,
  fetchAdminTutorsApi,
  setReviewHiddenApi,
  setTutorSuspendedApi,
} from '~/features/admin/api/admin.api';

import type { AdminOrder, AdminReview, AdminTutor } from '~/features/admin/types/admin.type';

export interface AdminState {
  tutors: AdminTutor[];
  isLoadingTutors: boolean;
  orders: AdminOrder[];
  isLoadingOrders: boolean;
  reviews: AdminReview[];
  isLoadingReviews: boolean;
  isSubmitting: boolean;
}

const initialState: AdminState = {
  tutors: [],
  isLoadingTutors: false,
  orders: [],
  isLoadingOrders: false,
  reviews: [],
  isLoadingReviews: false,
  isSubmitting: false,
};

export const fetchAdminTutors = createAsyncThunk<AdminTutor[], void>(
  'admin/fetchTutors',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAdminTutorsApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchAdminOrders = createAsyncThunk<AdminOrder[], void>(
  'admin/fetchOrders',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAdminOrdersApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchAdminReviews = createAsyncThunk<AdminReview[], string>(
  'admin/fetchReviews',
  async (tutorId, { rejectWithValue }) => {
    try {
      return await fetchAdminReviewsApi(tutorId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * 조치 계열 thunk 는 성공 후 목록을 **다시 불러온다.**
 * @description 화면에서만 상태를 바꾸면 집계(평점·매출)가 함께 움직이지 않아 숫자가 어긋난다 —
 *  예를 들어 후기를 숨기면 평점이 다시 계산되는데, 그 값은 DB 만 알고 있다.
 */
export const confirmOrderPaid = createAsyncThunk<void, string>(
  'admin/confirmOrderPaid',
  async (orderId, { dispatch, rejectWithValue }) => {
    try {
      await confirmOrderPaidApi(orderId);
      await dispatch(fetchAdminOrders()).unwrap();
    } catch (err) {
      return rejectWithValue(err);
    }
    return undefined;
  },
);

export const setReviewHidden = createAsyncThunk<
  void,
  { reviewId: string; isHidden: boolean; tutorId: string }
>(
  'admin/setReviewHidden',
  async ({ reviewId, isHidden, tutorId }, { dispatch, rejectWithValue }) => {
    try {
      await setReviewHiddenApi(reviewId, isHidden);
      await dispatch(fetchAdminReviews(tutorId)).unwrap();
      await dispatch(fetchAdminTutors()).unwrap();
    } catch (err) {
      return rejectWithValue(err);
    }
    return undefined;
  },
);

export const setTutorSuspended = createAsyncThunk<
  void,
  { tutorId: string; isSuspended: boolean; reason?: string }
>(
  'admin/setTutorSuspended',
  async ({ tutorId, isSuspended, reason }, { dispatch, rejectWithValue }) => {
    try {
      await setTutorSuspendedApi(tutorId, isSuspended, reason);
      await dispatch(fetchAdminTutors()).unwrap();
    } catch (err) {
      return rejectWithValue(err);
    }
    return undefined;
  },
);

const AdminSlice = createSlice({
  initialState,
  name: 'admin',
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchAdminTutors.pending, state => {
        state.isLoadingTutors = true;
      })
      .addCase(fetchAdminTutors.fulfilled, (state, action) => {
        state.tutors = action.payload;
        state.isLoadingTutors = false;
      })
      .addCase(fetchAdminTutors.rejected, state => {
        state.isLoadingTutors = false;
      })
      .addCase(fetchAdminOrders.pending, state => {
        state.isLoadingOrders = true;
      })
      .addCase(fetchAdminOrders.fulfilled, (state, action) => {
        state.orders = action.payload;
        state.isLoadingOrders = false;
      })
      .addCase(fetchAdminOrders.rejected, state => {
        state.isLoadingOrders = false;
      })
      .addCase(fetchAdminReviews.pending, state => {
        state.isLoadingReviews = true;
      })
      .addCase(fetchAdminReviews.fulfilled, (state, action) => {
        state.reviews = action.payload;
        state.isLoadingReviews = false;
      })
      .addCase(fetchAdminReviews.rejected, state => {
        state.isLoadingReviews = false;
      })
      .addCase(confirmOrderPaid.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(confirmOrderPaid.fulfilled, state => {
        state.isSubmitting = false;
      })
      .addCase(confirmOrderPaid.rejected, state => {
        state.isSubmitting = false;
      })
      .addCase(setReviewHidden.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(setReviewHidden.fulfilled, state => {
        state.isSubmitting = false;
      })
      .addCase(setReviewHidden.rejected, state => {
        state.isSubmitting = false;
      })
      .addCase(setTutorSuspended.pending, state => {
        state.isSubmitting = true;
      })
      .addCase(setTutorSuspended.fulfilled, state => {
        state.isSubmitting = false;
      })
      .addCase(setTutorSuspended.rejected, state => {
        state.isSubmitting = false;
      });
  },
});

export default AdminSlice;
